import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useStripe, useElements, ExpressCheckoutElement } from '@stripe/react-stripe-js'
import {
  StripeError,
  StripeExpressCheckoutElementReadyEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementClickEvent,
  ClickResolveDetails,
  StripeExpressCheckoutElementOptions,
} from '@stripe/stripe-js'

import { TGetItemFn } from '@/config/types'
import { CustomDOMEvents } from '@/enums/customDOMEvents'
import { PageName } from '@/enums/pageName'
import {
  CheckoutFlowVersion,
  PaymentMethod as PaymentMethodEnum,
  PaymentPlatform,
} from '@/enums/payments'
import { prepareContactStateRoutesMap } from '@/utils/paymentScreenUtils'
import { getPath } from '@/utils/urlUtils'

import { ApiService } from '@/api'
import { TContactAccessState, TSubscribeStripeV2Payload } from '@/api/types'
import { emailKey } from '@/auth/types'
import { useAuth } from '@/auth/useAuth'
import { resetContactStateCheckTimeout } from '@/commonPages/paymentScreenPage/hooks/contactState/useCheckContactState'
import { contactNameKey } from '@/core/constants'
import { ISessionContext } from '@/core/session'
import { useSession } from '@/core/session/useSession'
import { trackPaymentInfoAdded, trackPurchaseStarted } from '@/events/events'

import { TWalletPaymentButtonV2Props } from './types'

const getResultUrl = (
  paymentResultUrl: string,
  paymentMethod: PaymentMethodEnum,
  successPage: PageName
): URL => {
  const fail = PageName.paymentFailed
  const url = new URL(paymentResultUrl)
  url.searchParams.append('success_page', successPage)
  url.searchParams.append('fail_page', fail)
  url.searchParams.append('payment_method', paymentMethod)

  return url
}

function prepareSubscribePayload(
  getItem: TGetItemFn,
  email: string,
  sessionContext: ISessionContext,
  config: IConfig,
  internalPlanId: string
): TSubscribeStripeV2Payload {
  const contactName = getItem(contactNameKey)
  const sessionId = sessionContext.sessionId
  const utmParams = sessionContext.utmParams
  const fbParams = sessionContext.fbParams || {}
  const gaParams = sessionContext.gaParams || {}
  const firstVisitAt = sessionContext.fva
  const firstVisitAtDiff: number = +new Date() - +new Date(firstVisitAt)

  const testEmail = getItem('testEmail')
  if (testEmail) {
    console.log(`testEmail '${testEmail} detected`)
  }

  return {
    session_id: sessionId,
    page_set_id: config.PAGE_SET_ID,
    email: email,
    test_email: testEmail,
    utm_params: {
      ...utmParams,
      ...fbParams,
      ...gaParams,
    },
    first_visit_at_diff: firstVisitAtDiff,
    name: contactName,
    plan_id: internalPlanId,
  }
}

const eventOnStripeWalletButtonClick = new Event(CustomDOMEvents.ON_STRIPE_WALLET_BUTTON_CLICK)

export const WalletPaymentButtonV2 = ({
  planData,
  getItem,
  setItem,
  showDelimiter,
  buttonsDelimiterText,
  pageName,
  config,
  stripeButtonTheme,
  getStripeWalletButtonStyles,
  onReady,
  successRedirectTo,
  contactStateRoutesMap,
}: TWalletPaymentButtonV2Props) => {
  const { user, logout } = useAuth()
  const sessionContext = useSession()
  const navigate = useNavigate()
  const stripe = useStripe()
  const elements = useElements()
  const [walletAvailable, setWalletAvailable] = useState<
    PaymentMethodEnum.APPLE_PAY | PaymentMethodEnum.GOOGLE_PAY | false | undefined
  >(undefined)

  const stripeButtonStyles = useMemo<StripeExpressCheckoutElementOptions>(
    () => getStripeWalletButtonStyles(stripeButtonTheme, CheckoutFlowVersion.V2),
    [getStripeWalletButtonStyles, stripeButtonTheme]
  )

  const onReadyFn = (event: StripeExpressCheckoutElementReadyEvent): void => {
    if (!event.availablePaymentMethods) {
      // No buttons will show
      setWalletAvailable(false)
    } else {
      if (event.availablePaymentMethods.googlePay) {
        setWalletAvailable(PaymentMethodEnum.GOOGLE_PAY)
      } else if (event.availablePaymentMethods.applePay) {
        setWalletAvailable(PaymentMethodEnum.APPLE_PAY)
      }
    }
    if (typeof onReady === 'function') {
      onReady(event)
    }
  }

  const onClick = ({ resolve }: StripeExpressCheckoutElementClickEvent) => {
    document.dispatchEvent(eventOnStripeWalletButtonClick)
    const amount = Number.isFinite(planData.trialPrice) ? planData.trialPrice : planData.price
    const options: ClickResolveDetails = {
      emailRequired: true,
      lineItems: [
        {
          name: planData.orderPlanName,
          amount: parseFloat((amount * 100).toFixed(2)),
        },
      ],
    }
    resolve(options)
  }

  const onLoadError = (): void => {}

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent): Promise<void> => {
    if (!stripe || !elements) {
      // Stripe.js hasn't loaded yet.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    if (!event.billingDetails?.email) {
      console.log('no email')
      navigate(getPath(successRedirectTo))
      return
    }

    const { error: submitError } = await elements.submit()
    if (submitError) {
      console.log(submitError.message)
      // TODO: redirect?
      return
    }

    let paymentMethod: PaymentMethodEnum
    switch (event.expressPaymentType) {
      case 'apple_pay':
        paymentMethod = PaymentMethodEnum.APPLE_PAY
        break
      case 'google_pay':
        paymentMethod = PaymentMethodEnum.GOOGLE_PAY
        break
      default:
        throw Error('Unsupported payment method')
    }
    setItem('paymentMethod', paymentMethod)
    setItem('paymentPlatform', PaymentPlatform.STRIPE)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const promisesList: Promise<any>[] = [
      trackPurchaseStarted(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        planData.internalPlanId,
        paymentMethod,
        PaymentPlatform.STRIPE,
        sessionContext,
        user
      ),
    ]

    promisesList.push(
      trackPaymentInfoAdded(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        planData.internalPlanId,
        paymentMethod,
        PaymentPlatform.STRIPE,
        sessionContext,
        config.PIXEL_ID,
        config.TWITTER_CONF?.pixel_id,
        config.TIKTOK_PIXEL_ID,
        user,
        [],
        [event.expressPaymentType]
      )
    )

    const redirectToResultPage = async (
      paymentResultUrl: string,
      type: string,
      clientSecret: string
    ) => {
      const redirectTo = getResultUrl(paymentResultUrl, paymentMethod, successRedirectTo)

      if (type === 'setup') {
        const intent = await stripe.retrieveSetupIntent(clientSecret)
        if (intent.error) {
          navigate(getPath(PageName.paymentFailed))
          return
        }
        redirectTo.searchParams.append('setup_intent', intent.setupIntent.id)
      } else {
        const intent = await stripe.retrievePaymentIntent(clientSecret)
        if (intent.error) {
          navigate(getPath(PageName.paymentFailed))
          return
        }
        redirectTo.searchParams.append('payment_intent', intent.paymentIntent.id)
      }
      Promise.all(promisesList).finally(() => {
        window.location.href = redirectTo.href
      })
    }

    resetContactStateCheckTimeout(setItem)
    //  wallet_name: event.expressPaymentType,
    const payload = prepareSubscribePayload(
      getItem,
      event.billingDetails?.email,
      sessionContext,
      config,
      planData.internalPlanId
    )

    let response
    try {
      response = await ApiService.subscribeStripeV2(payload)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log('subscribe error', error)

      await logout()

      setItem(emailKey, error.response?.data?.email ? error.response.data.email : '')

      if (error.response?.status === 409) {
        const state = error.response?.data?.state
        if (state) {
          const routesMap = prepareContactStateRoutesMap(contactStateRoutesMap || {})
          const path = routesMap[state as TContactAccessState]
          if (path) {
            navigate(path)
            return
          }
        }
      }
      navigate(getPath(PageName.paymentFailed))
      return
    }

    const {
      type,
      client_secret: clientSecret,
      payment_result_url: paymentResultUrl,
      email: responseEmail,
    } = response.data
    setItem(emailKey, responseEmail)

    let stripeError: StripeError | undefined
    const confirmSubscription = type === 'setup' ? stripe.confirmSetup : stripe.confirmPayment

    try {
      const result = await confirmSubscription({
        elements,
        clientSecret,
        confirmParams: {
          return_url: getResultUrl(paymentResultUrl, paymentMethod, successRedirectTo).href,
        },
      })

      if (result.error) {
        stripeError = result.error
      }

      if (stripeError) {
        redirectToResultPage(paymentResultUrl, type, clientSecret)
        // Show error to your customer (e.g., insufficient funds)
        return
      }
    } catch (error) {
      console.error('error2', error)
      redirectToResultPage(paymentResultUrl, type, clientSecret)
    }
  }

  let containerClassName = 'stripe-wallet-container'
  if (walletAvailable === PaymentMethodEnum.GOOGLE_PAY) {
    containerClassName += ' gp-wallet-container'
  } else if (walletAvailable === false) {
    return null
  }

  return (
    <>
      {showDelimiter && (
        <div className="wallet-payment-button-delimiter">{buttonsDelimiterText}</div>
      )}
      <div className={containerClassName}>
        <ExpressCheckoutElement
          options={stripeButtonStyles}
          onClick={onClick}
          onConfirm={onConfirm}
          onLoadError={onLoadError}
          onReady={onReadyFn}
        />
      </div>
    </>
  )
}
